import React from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Ticket, FileText, Users } from "lucide-react";
import { dashboard, type DashboardData } from "../api/client";
import { useQuery } from "../hooks/useApi";

const fallback: DashboardData = {
  open_platform_tickets: 0,
  audit_events_last_24h: 0,
  total_users: 0,
  generated_at: "",
};

export function Dashboard() {
  const { data, isLoading } = useQuery(() => dashboard.get());

  const d: DashboardData = data ?? fallback;

  const kpis = [
    {
      icon: Ticket,
      label: "Open platform tickets",
      // Stable hook for the E2E "real function" assertion (epic 012 / story 15)
      // — lets the smoke test read the resolved KPI value deterministically
      // instead of scraping page text, so a silent 401 (bug 0057) is caught.
      testId: "kpi-open-platform-tickets",
      value: isLoading ? "—" : String(d.open_platform_tickets),
      valueType:
        d.open_platform_tickets > 0
          ? ("destructive" as const)
          : ("default" as const),
    },
    {
      icon: FileText,
      label: "Audit events (last 24h)",
      testId: "kpi-audit-events-24h",
      value: isLoading ? "—" : String(d.audit_events_last_24h),
      valueType: "default" as const,
    },
    {
      icon: Users,
      label: "Total users",
      testId: "kpi-total-users",
      value: isLoading ? "—" : String(d.total_users),
      valueType: "default" as const,
    },
  ];

  return (
    <AppShell
      breadcrumbs={["Dashboard"]}
      activeMenuItem="Dashboard"
      notificationCount={0}
    >
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1
            className="text-duvi-text-primary mb-1"
            style={{
              fontSize: "var(--text-xl)",
              fontWeight: "var(--font-weight-semibold)",
              lineHeight: "var(--line-height-heading)",
            }}
          >
            Dashboard
          </h1>
          <p
            className="text-duvi-text-secondary"
            style={{ fontSize: "var(--text-sm)" }}
          >
            Platform overview
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {kpis.map((kpi, index) => (
            <Card key={index} className="p-5">
              <div className="flex items-start justify-between mb-3">
                <kpi.icon className="w-5 h-5 text-duvi-text-secondary" />
              </div>
              <div
                className="text-duvi-text-secondary mb-2"
                style={{ fontSize: "var(--text-xs)" }}
              >
                {kpi.label}
              </div>
              <div
                data-testid={kpi.testId}
                className={
                  kpi.valueType === "destructive"
                    ? "text-duvi-destructive"
                    : "text-duvi-text-primary"
                }
                style={{
                  fontSize: "var(--text-2xl)",
                  fontWeight: "var(--font-weight-bold)",
                  lineHeight: "var(--line-height-heading)",
                }}
              >
                {kpi.value}
              </div>
            </Card>
          ))}
        </div>

        {d.generated_at && (
          <p
            className="text-duvi-text-secondary text-right"
            style={{ fontSize: "var(--text-xs)" }}
          >
            Last refreshed: {new Date(d.generated_at).toLocaleString()}
          </p>
        )}
      </div>
    </AppShell>
  );
}
